/**
 * Firebase singletons: auth and Firestore handles.
 *
 * Project selected at build time via `__FIREBASE_PROJECT__` (default
 * stc-production-150269; stc-nonproduction for the non-prod project). Database selected at build time via the
 * `__FIRESTORE_DATABASE_ID__` constant, baked in by esbuild's `--define`
 * mechanism (see scripts/build.sh). Prod build → "(default)"; dev build →
 * "develop" (OMON-091).
 *
 * The Firebase API key is a public Firebase web key and is safe to include
 * in client bundles — all access control is enforced by Firestore security
 * rules, not by key secrecy. See claude-memory/MEMORY.md "GCP API Keys".
 *
 * Extracted from main.ts as part of OMON-031 Phase 3; databaseId switch
 * added in OMON-091.
 */

import { initializeApp } from "firebase/app";
import { getAuth } from "firebase/auth";
import { getFirestore } from "firebase/firestore";
import { getStorage } from "firebase/storage";
import { appEnvForBuild, type AppEnv } from "./env-gate";

// Build-time substituted constants. Default values below are for
// `npm run watch` / local dev when build.sh hasn't run; the production
// build always overrides via --define.
declare const __FIRESTORE_DATABASE_ID__: string;
declare const __DEFAULT_DATA_SCOPE__: string;
// ADR-0044: the Identity Platform tenant a training build addresses. Empty
// (every non-training build) = the main auth pool, behaviour unchanged.
declare const __AUTH_TENANT_ID__: string;
// 2026-10-10 (the non-prod project arc): WHICH Firebase project this bundle is
// built for. Absent/production = every existing build, unchanged. A non-prod
// push token, login and database all belong to ONE Firebase project, so a
// non-prod planner must be built against stc-nonproduction's own web app.
declare const __FIREBASE_PROJECT__: string;

const FIREBASE_CONFIGS: Record<string, { apiKey: string; authDomain: string; projectId: string; storageBucket?: string }> = {
  "stc-production-150269": {
    apiKey: "AIzaSyC6rWNeLXtlHO8rnZ2rsI8Bpmgnw8ui1us",
    authDomain: "stc-production-150269.firebaseapp.com",
    projectId: "stc-production-150269",
    // OMON-217: device catalogue photos live in Cloud Storage and are read via
    // the authenticated Storage SDK (the bucket blocks public access). Needed so
    // getStorage() resolves the right bucket.
    storageBucket: "stc-production-150269.appspot.com",
  },
  // infra/terraform/firebase-project.tf output firebase_web_app (public web
  // key, like production's). No Storage bucket yet: catalogue photos are not
  // available on non-prod until one is made.
  "stc-nonproduction": {
    apiKey: "AIzaSyBpiy863gMYhMt3_RpIV-HQR-UlkG8fQww",
    authDomain: "stc-nonproduction.firebaseapp.com",
    projectId: "stc-nonproduction",
  },
};

/** The Firebase project this bundle was BUILT for. */
export const firebaseProject: string =
  typeof __FIREBASE_PROJECT__ !== "undefined" && __FIREBASE_PROJECT__ !== ""
    ? __FIREBASE_PROJECT__
    : "stc-production-150269";

const firebaseConfig = FIREBASE_CONFIGS[firebaseProject];
if (!firebaseConfig) {
  // Fail loudly at load: never fall back to production for an unknown project.
  throw new Error(`No Firebase config for project ${JSON.stringify(firebaseProject)}`);
}

// Export `app` so callers can pass it to `getFunctions(app, region)` for
// Firebase Functions v2 callables (OMON-144). Other consumers don't need
// the app reference directly — they use the `auth` / `db` singletons below.
export const app = initializeApp(firebaseConfig);

export const auth = getAuth(app);
// ADR-0044: set BEFORE any sign-in/session-restore so every auth operation
// is tenant-scoped on training builds. A training credential then does not
// exist on any other surface — refused by Firebase itself, ahead of env-gate.
if (typeof __AUTH_TENANT_ID__ !== "undefined" && __AUTH_TENANT_ID__ !== "") {
  auth.tenantId = __AUTH_TENANT_ID__;
}

// OMON-217: Cloud Storage handle for device catalogue photos. The bucket has
// public access prevented, so images are fetched via authenticated
// getDownloadURL (tokenised URL) rather than raw storage.googleapis.com URLs.
export const storage = getStorage(app);

// Single-arg getFirestore for "(default)"; second arg selects a named DB.
// The constant is substituted at build time; the runtime check on
// "(default)" keeps the prod code path identical to pre-OMON-091.
/** The database id this bundle was BUILT for — exported so the environment
 * gate (env-gate.ts via auth.ts) compares tokens against the build, not a
 * runtime guess. */
export const firestoreDatabaseId: string = __FIRESTORE_DATABASE_ID__;

/** The ONE environment this build belongs to (env-gate appEnvForBuild):
 * project first (stc-nonproduction → nonprod), then the database name. Every
 * caller uses this, so the gate, the menu and the role policy can't disagree. */
export const appEnv: AppEnv = appEnvForBuild(firebaseProject, firestoreDatabaseId);

export const db = __FIRESTORE_DATABASE_ID__ === "(default)"
  ? getFirestore(app)
  : getFirestore(app, __FIRESTORE_DATABASE_ID__);

// Surfaces the build's default data-scope claim expectation to app code.
// Consumed by OMON-027 (user role + dataScope) once that lands; harmless
// to expose now.
export const defaultDataScope: "real" | "test" | "both" =
  __DEFAULT_DATA_SCOPE__ as "real" | "test" | "both";
